import {
  phone as phoneLib, phoneListPayload, phoneListRows, phoneRelationLabel, UNKNOWN_RELATION,
} from '@parsina/framework';

import { toEnDigits } from './utils';

/**
 * A patient's numbers, as this panel reads and writes them (PM-0536).
 *
 * A record holds up to five numbers, each with «صاحب شماره» — whose number it is. The
 * primary is mirrored into `Patient.phone`, so every screen that shows one number keeps
 * showing the same one; this module is for the screens that show or edit them all.
 *
 * The relation is not decoration. Only the patient's own numbers («خود بیمار», and the
 * «نامشخص» every number from before the list reads) find the record in the patient app,
 * so those are the ones the desk is asked about when another record already holds them.
 * A spouse's or a parent's number never claims anything and never blocks a save.
 *
 * Every rule here is the clinic BFF's (src/lib/patientPhones.js there) and, behind it,
 * patientmanagement's. This module only says early what the server would refuse, and
 * turns a refusal's code into the sentence under the row it is about.
 */

/** The service's cap on live numbers per record. */
export const MAX_PHONES = 5;

/**
 * Everything a single record shows about its numbers, and the etag of that list — what
 * a save sends back, so a list that moved under the open form is refused, not
 * overwritten (PHONES_CHANGED).
 */
export const PATIENT_PHONES_FIELDS = `
  phones {
    id number raw relation label isPrimary verified locked landline source addedHere sharedCount
    optedOut whatsappKnown
  }
  phonesEtag
`;

/**
 * What a list row needs: the primary, how many others, and which one a search matched —
 * and what decides which number a message to the patient goes to (messageNumber), so
 * the compose dialog counts a broadcast by the numbers it will actually reach.
 */
export const PATIENT_PHONES_LIST_FIELDS = `
  phones { id number raw relation isPrimary verified landline addedHere optedOut whatsappKnown }
`;

// The relations that make a number the record's own — the ones that claim it.
const OWN_RELATIONS = new Set(['self', UNKNOWN_RELATION]);

export const isOwnRelation = (relation) => OWN_RELATIONS.has(relation);

/** A number as the lookups key it: E.164, or '' while it does not parse as a mobile. */
export const phoneKey = (number) =>
  (number ? phoneLib.normalizePhone(String(number).trim()) || '' : '');

/**
 * The number a row puts on the wire: E.164 for a mobile however it was typed (a voice
 * transcript and a prefill write it raw), a landline as typed — the service keeps a
 * landline as it was written.
 */
export const wireNumber = (row) => (row.landline
  ? toEnDigits(String(row.number || '')).trim()
  : phoneKey(row.number) || toEnDigits(String(row.number || '')).trim());

/**
 * Whether the desk has to answer for the records already on this row's number before
 * the server takes it: the primary, or a number that is the patient's own — what the
 * BFF asks about. A relative's number is only ever mentioned.
 */
export const rowBlocks = (row) => Boolean(row?.isPrimary) || isOwnRelation(row?.relation);

/**
 * The `PatientPhoneInput` list a save sends: the kit's (`phoneListPayload` — the whole
 * list, exactly one primary, labels sent back whole) with each number in its wire form.
 */
export const phonesPayload = (rows) =>
  phoneListPayload(rows).map((row, i) => ({ ...row, number: wireNumber({ ...rows?.[i], number: row.number }) }));

/**
 * Whether the record's numbers may be edited as one list: they were read, with their
 * etag. Without it — the BFF could not read the list and answered the mirrored `phone`,
 * or the record was read without them — a list save would be refused, or replace rows
 * nobody saw, so the editor offers the main number alone, saved as `phone` as before
 * the list.
 */
export const phonesEditable = (patient) => Array.isArray(patient?.phones) && Boolean(patient?.phonesEtag);

/** The rows the record's editor starts from: its numbers as loaded, primary first. */
export const phoneRowsOf = (phones) => phoneListRows(phones);

/** The editor's rows for a record: its numbers, or one blank «خود بیمار» row. */
export const patientPhoneRows = (patient) => phoneRowsOf(phonesOf(patient));

/**
 * The list to save, or null when the desk changed nothing in it — `phones` is a whole-
 * list write, so an untouched list is left out of the patch rather than rewritten.
 */
export function phonesPatch(rows, patient) {
  const next = phonesPayload(rows);
  const before = phonesPayload(patientPhoneRows(patient));
  return JSON.stringify(next) === JSON.stringify(before) ? null : next;
}

/**
 * The desk's rows carried over a record that lost a number meanwhile («بیمار گزارش
 * داد»): rows the record no longer holds are dropped and every other edit is kept; when
 * the primary went, the record's new primary is the primary again.
 */
export function carryRows(rows, patient) {
  const stored = phonesOf(patient);
  const ids = new Set(stored.map((p) => p.id).filter(Boolean));
  const kept = rows.filter((row) => !row.id || ids.has(row.id));
  if (!kept.length) return patientPhoneRows(patient);
  if (kept.some((row) => row.isPrimary)) return kept;
  const primaryId = stored.find((p) => p.isPrimary)?.id;
  const next = kept.map((row) => ({ ...row, isPrimary: Boolean(primaryId) && row.id === primaryId }));
  if (!next.some((row) => row.isPrimary)) next[0] = { ...next[0], isPrimary: true };
  return next;
}

/**
 * The record's live numbers, primary first, as the BFF sends them — or, for a record
 * read without them (a list row, a BFF from before the list), its single `phone` as
 * the primary alone, the way the BFF itself answers when it cannot read the list.
 */
export function phonesOf(patient) {
  if (Array.isArray(patient?.phones) && patient.phones.length) return patient.phones;
  if (!patient?.phone) return [];
  return [{
    id: '', number: phoneLib.normalizePhone(patient.phone) || null, raw: patient.phone,
    relation: UNKNOWN_RELATION, label: '', isPrimary: true, verified: false, locked: false,
    landline: false, source: 'legacy', addedHere: false, sharedCount: null,
    optedOut: false, whatsappKnown: 'unknown',
  }];
}

/** The numbers beside the primary — what «+N» counts. */
export const otherPhones = (patient) => phonesOf(patient).filter((p) => !p.isPrimary);

/** How a stored number reads: its E.164 when it parsed, else as it was typed. */
export const storedNumber = (p) => p?.number || p?.raw || '';

/**
 * «+N»'s title on a desktop: every other number with whose it is, one per line —
 * «۰۹۱۲… (همسر)». A «نامشخص» number is shown without a word, as every number from before
 * the list is one.
 */
export function otherPhonesTitle(patient, { locale, formatDigits }) {
  return otherPhones(patient).map((p) => {
    const number = formatDigits(phoneLib.forDisplay(storedNumber(p)));
    const relation = p.relation !== UNKNOWN_RELATION ? phoneRelationLabel(p.relation, locale) : '';
    return relation ? `${number} (${relation})` : number;
  }).join('\n');
}

/**
 * The number a search found this patient by, when it is NOT the one the row shows: the
 * query's digits (four or more, any keyboard) inside one of the patient's other numbers.
 * The service matches every number; this only says which, from `phones`, with no field
 * of its own. Null when the primary matched, or the query is not a number.
 */
export function matchedOtherPhone(patient, query) {
  const digits = toEnDigits(String(query || '')).replace(/\D/g, '');
  if (digits.length < 4) return null;
  const hit = (p) => {
    const all = toEnDigits(storedNumber(p)).replace(/\D/g, '');
    const national = toEnDigits(phoneLib.forDisplay(storedNumber(p))).replace(/\D/g, '');
    return all.includes(digits) || national.includes(digits);
  };
  const primary = phonesOf(patient).find((p) => p.isPrimary);
  if (primary && hit(primary)) return null;
  if (!primary && patient?.phone && hit({ raw: patient.phone })) return null;
  return otherPhones(patient).find(hit) || null;
}

// An ASCII number inside a sentence keeps its own direction (U+2068 FSI … U+2069 PDI).
const isolate = (s) => `\u2068${s}\u2069`;

/** A stored number as a sentence shows it: national form, the page's digits, isolated. */
export const phoneText = (p, { formatDigits }) =>
  isolate(formatDigits(phoneLib.forDisplay(storedNumber(p))));

/**
 * The number a search result shows, as one meta-line part: the primary — or, when the
 * search found the patient by another of their numbers, that number with «یافته با
 * شمارهٔ دیگر» (and whose it is), so the desk sees why the row is there. '' with no
 * number at all. For the پذیرش typeahead on both tiers.
 */
export function searchPhoneText(patient, query, { t, locale, formatDigits }) {
  const matched = matchedOtherPhone(patient, query);
  if (!matched) return patient?.phone ? formatDigits(phoneLib.forDisplay(patient.phone)) : '';
  const phone = phoneText(matched, { formatDigits });
  const relation = matched.relation !== UNKNOWN_RELATION ? phoneRelationLabel(matched.relation, locale) : '';
  return relation
    ? t('patients.phones.matchedOnRelation', { phone, relation })
    : t('patients.phones.matchedOn', { phone });
}

// ── Messages: which number, and «پیام نفرست» (PM-0536 P6) ──────────────────────

/**
 * Why THIS clinic stops messaging one number («پیام نفرست», D9) — the clinic BFF's
 * PHONE_OPT_OUT_REASONS. «شمارهٔ اشتباه» here is about this number only; the link's
 * «عدم تماس» stays the patient-wide switch.
 */
export const PHONE_OPT_OUT_REASONS = ['patient_request', 'wrong_number', 'not_theirs', 'other'];

export const PHONE_OPT_OUT_LABEL_KEY = {
  patient_request: 'patients.phones.optOut.reason.patientRequest',
  wrong_number: 'patients.phones.optOut.reason.wrongNumber',
  not_theirs: 'patients.phones.optOut.reason.notTheirs',
  other: 'patients.phones.optOut.reason.other',
};

const IRAN_PREFIX = '+98';
const isIranian = (p) => String(p?.number || '').startsWith(IRAN_PREFIX);
// Ten digits starting with 9 after the plus: the SMS service reads them as an Iranian
// mobile that lost its zero, so no SMS goes to such a number (the BFF's READ_AS_IRANIAN).
const READ_AS_IRANIAN = /^\+9\d{9}$/;

/**
 * Whether the record's numbers were read — a list whose rows carry their ids. A record
 * read without them (a search result, a BFF from before the list) has `phone` alone,
 * which messages go to as they always did.
 */
export const numbersRead = (patient) =>
  Array.isArray(patient?.phones) && patient.phones.every((p) => Boolean(p.id));

/**
 * Why `phone` cannot carry a message on `channel` from this clinic, or null when it can:
 * `optedOut` («پیام نفرست»), `notOnWhatsapp`, `landline`, or `channel` — a number this
 * channel has no reading for (Bale is +98 only; an unparsed number goes nowhere in Iran).
 *
 * @param route { foreign } — the clinic is outside Iran, where a nationally written
 *              number nothing parsed yet is still read in the clinic's own country.
 */
export function numberBlocker(phone, channel, { foreign = false } = {}) {
  if (!phone) return 'channel';
  if (phone.optedOut) return 'optedOut';
  if (phone.landline) return 'landline';
  if (!phone.number && !foreign) return 'channel';
  if (channel === 'bale' && !isIranian(phone)) return 'channel';
  if (channel === 'sms' && READ_AS_IRANIAN.test(phone.number || '')) return 'channel';
  if (channel === 'whatsapp' && phone.whatsappKnown === 'no') return 'notOnWhatsapp';
  return null;
}

// The fallback order: the patient's own number before a relative's; on WhatsApp one the
// gateway said «yes» for before one never asked; a proven number first; then the order
// the record lists them in (primary, then oldest) — Array#sort keeps it.
const healthier = (channel) => (a, b) =>
  Number(isOwnRelation(b.relation || UNKNOWN_RELATION)) - Number(isOwnRelation(a.relation || UNKNOWN_RELATION))
  || (channel === 'whatsapp' ? Number(b.whatsappKnown === 'yes') - Number(a.whatsappKnown === 'yes') : 0)
  || Number(Boolean(b.verified)) - Number(Boolean(a.verified));

/**
 * The ONE number a message to this patient goes to on `channel`, or why there is none.
 *
 * MIRRORS the clinic BFF's numberFor (src/lib/patientAudience.js), which decides what is
 * actually sent and priced — change both. The desk's pick (`phoneId`); else the primary;
 * else a fallback, which only a number somebody proved or THIS clinic added may be, the
 * patient's own before a relative's; for SMS a +98 number first; never a number this
 * clinic opted out. Two things the BFF knows and this does not, so it can differ there
 * alone: which of two proven numbers was proved last, and the country a nationally
 * written number was entered in (a clinic outside Iran reads every such number).
 * «عدم تماس» is the caller's (reachBlocker): it is the link's, not a number's.
 *
 * @returns {{ phone: object|null, reason: string|null }} — `phone` a PatientPhone (for a
 *          record whose numbers were not read, its `phone` as the primary alone, id '');
 *          `reason`: noPhone | optedOut | notOnWhatsapp | changed (the picked number is
 *          no longer on the record).
 */
export function messageNumber(patient, channel, { phoneId = '', foreign = false } = {}) {
  const refused = (reason) => ({ phone: null, reason });
  if (!numbersRead(patient)) {
    if (!patient?.phone) return refused('noPhone');
    if (phoneId) return refused('changed');
    const whatsappKnown = patient.whatsappKnown || patient.contact?.whatsappKnown;
    if (channel === 'whatsapp' && whatsappKnown === 'no') return refused('notOnWhatsapp');
    return { phone: phonesOf({ phone: patient.phone })[0], reason: null };
  }
  const route = { foreign };
  const phones = patient.phones;
  const blocked = (p) => numberBlocker(p, channel, route);
  // A number no channel here can use reads as no number for that channel.
  const asReason = (why) => (why === 'optedOut' || why === 'notOnWhatsapp' ? why : 'noPhone');
  if (phoneId) {
    const picked = phones.find((p) => p.id === phoneId);
    if (!picked) return refused('changed');
    return blocked(picked) ? refused(asReason(blocked(picked))) : { phone: picked, reason: null };
  }
  if (!phones.length) return refused('noPhone');
  const primary = phones.find((p) => p.isPrimary) || null;
  const usable = phones.filter((p) => !blocked(p));
  const fallbacks = usable
    .filter((p) => p !== primary && (p.verified || p.addedHere))
    .sort(healthier(channel));
  let eligible = usable.includes(primary) ? [primary, ...fallbacks] : fallbacks;
  if (channel === 'sms') eligible = [...eligible.filter(isIranian), ...eligible.filter((p) => !isIranian(p))];
  if (eligible.length) return { phone: eligible[0], reason: null };
  return refused(asReason(blocked(primary || phones[0])));
}

/** The key a household shares: the routed number, in the lookups' one spelling of it. */
export const routedKey = (phone) => phoneKey(storedNumber(phone)) || storedNumber(phone) || null;

// ── Refusals ─────────────────────────────────────────────────────────────────

/**
 * What a save's refusal says about the numbers, or null when it is about something
 * else. The BFF names a refused row by its `index` in the list sent; this turns that
 * into the row's key, so the sentence lands under the row.
 *
 *   { rowErrors: { [key]: message }, alert: message | '', code, conflicts, reload }
 *
 * `conflicts` (PHONE_HAS_PATIENTS) are the server's own candidates per number — the
 * caller seeds its lookups with them; `reload` (PHONES_CHANGED) asks the caller to read
 * the list again, because it moved while the form was open.
 */
export function phonesRefusal(err, rows, t) {
  const ext = err?.extensions || {};
  const code = ext.code;
  const keyAt = (index) => (Number.isInteger(index) && rows?.[index] ? rows[index].key : null);
  const onRow = (message) => {
    const key = keyAt(ext.index);
    return key ? { rowErrors: { [key]: message }, alert: '' } : { rowErrors: {}, alert: message };
  };
  const out = (body) => ({ code, conflicts: [], reload: false, rowErrors: {}, alert: '', ...body });
  switch (code) {
    case 'PHONE_HAS_PATIENTS':
      return out({ conflicts: phoneConflicts(err) });
    case 'PHONE_INVALID':
      return out(onRow(t(ext.reason === 'primary' ? 'patients.phones.errors.onePrimary' : 'validation.phone.invalid')));
    case 'PHONE_DUPLICATE':
      return out(onRow(t('patients.phones.errors.duplicate')));
    case 'PHONE_RELATION_INVALID':
      return out(onRow(t('patients.phones.errors.relationRequired')));
    case 'PHONE_LIMIT':
      return out({ alert: t('patients.phones.errors.limit', { count: ext.max || MAX_PHONES }) });
    case 'PHONE_LOCKED':
      return out({ alert: t(ext.reason === 'signin' ? 'patients.phones.errors.lockedSignin' : 'patients.phones.errors.locked') });
    case 'PHONE_LAST':
      return out({ alert: t('patients.phones.errors.cannotRemoveLast') });
    case 'PHONES_CHANGED':
      return out({ alert: t('patients.phones.errors.changed'), reload: true });
    case 'PHONE_LOOKUP_FAILED':
      return out({ alert: t('patients.phones.errors.lookupFailed') });
    case 'PATIENT_MERGED':
      return out({ alert: t('patients.phones.errors.merged') });
    case 'NOT_LINKED':
      return out({ alert: t('patients.phones.errors.notLinked') });
    case 'TEXT_TOO_LONG':
      return ext.field === 'label' ? out(onRow(t('patients.phones.errors.labelTooLong', { max: ext.max || 40 }))) : null;
    default:
      return null;
  }
}

/**
 * Every number a PHONE_HAS_PATIENTS names, with the records on it: the BFF's
 * `conflicts` (one per number), or — from a BFF before the list — its single
 * `phone` + `candidates`, which is the first row's.
 */
export function phoneConflicts(err) {
  const ext = err?.extensions || {};
  if (Array.isArray(ext.conflicts) && ext.conflicts.length) {
    return ext.conflicts.map((c) => ({
      phone: c.phone || '',
      index: Number.isInteger(c.index) ? c.index : null,
      candidates: Array.isArray(c.candidates) ? c.candidates : [],
    }));
  }
  return [{
    phone: ext.phone || '',
    index: 0,
    candidates: Array.isArray(ext.candidates) ? ext.candidates : [],
  }];
}

/** The row a conflict is about: by its number first (indexes shift), else its index. */
export function conflictRowIndex(conflict, rows) {
  const key = phoneKey(conflict.phone);
  const byNumber = key ? rows.findIndex((row) => phoneKey(row.number) === key) : -1;
  if (byNumber >= 0) return byNumber;
  return Number.isInteger(conflict.index) && rows[conflict.index] ? conflict.index : -1;
}
